<template>
  <div class="location-filter-widget">
    <div class="card h-100">
      <div class="card-header">
        <h6 class="mb-0">
          <i class="bi bi-geo-alt"></i>
          География
        </h6>
      </div>
      <div class="card-body">
        
        <!-- Выбор страны -->
        <div class="country-selection mb-3">
          <label class="form-label small">Страна:</label>
          <select class="form-select form-select-sm" v-model="internalSelectedCountry" @change="onCountryChange">
            <option value="">Все страны</option>
            <option v-for="country in countries" :key="country.name" :value="country.name">
              {{ country.name }} ({{ country.count }})
            </option>
          </select>
        </div>

        <!-- Выбор города (если выбрана страна) -->
        <div v-if="internalSelectedCountry" class="city-selection mb-3">
          <label class="form-label small">Город:</label>
          <select class="form-select form-select-sm" v-model="internalSelectedCity" @change="onCityChange">
            <option value="">Все города</option>
            <option v-for="city in availableCities" :key="city.name" :value="city.name">
              {{ city.name }} ({{ city.count }})
            </option>
          </select>
        </div>

        <!-- Популярные локации -->
        <div v-if="!internalSelectedCountry && popularLocations.length > 0" class="popular-locations">
          <label class="form-label small">Популярные:</label>
          <div class="location-badges">
            <button
              v-for="location in popularLocations"
              :key="`${location.country}-${location.city}`"
              class="btn btn-sm btn-outline-primary location-badge"
              @click="selectLocation(location.country, location.city)"
            >
              {{ location.city }}, {{ location.country }}
              <span class="badge bg-secondary ms-1">{{ location.count }}</span>
            </button>
          </div>
        </div>

        <!-- Статистика -->
        <div class="stats-summary mt-3 pt-3 border-top">
          <small class="text-muted">
            <div v-if="internalSelectedCountry && internalSelectedCity">
              {{ internalSelectedCity }}, {{ internalSelectedCountry }}:
              {{ getCityMaterialsCount(internalSelectedCountry, internalSelectedCity) }} материалов
            </div>
            <div v-else-if="internalSelectedCountry">
              {{ internalSelectedCountry }}:
              {{ getCountryMaterialsCount(internalSelectedCountry) }} материалов
            </div>
            <div v-else>
              Всего локаций: {{ locations.length }}
            </div>
          </small>
        </div>

        <!-- Добавить локацию -->
        <div class="add-location mt-2">
          <button class="btn btn-link btn-sm text-decoration-none p-0" @click="showAddModal">
            <i class="bi bi-plus-circle"></i>
            Добавить локацию
          </button>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, watch } from 'vue';

export default {
  name: 'LocationFilterWidget',
  props: {
    locations: {
      type: Array,
      default: () => []
    },
    selectedCountry: {
      type: String,
      default: ''
    },
    selectedCity: {
      type: String,
      default: ''
    }
  },
  emits: ['country-selected', 'city-selected', 'add-location'],
  setup(props, { emit }) {
    
    // Локальное состояние
    const internalSelectedCountry = ref(props.selectedCountry);
    const internalSelectedCity = ref(props.selectedCity);

    // Computed свойства
    const countries = computed(() => {
      if (!props.locations || !Array.isArray(props.locations)) return [];
      
      const countryMap = {};
      props.locations.forEach(location => {
        const countryName = location.country;
        if (!countryMap[countryName]) {
          countryMap[countryName] = { name: countryName, count: 0 };
        }
        countryMap[countryName].count += location.materials_count || 1;
      });
      
      return Object.values(countryMap).sort((a, b) => b.count - a.count);
    });

    const availableCities = computed(() => {
      if (!internalSelectedCountry.value || !props.locations) return [];
      
      return props.locations
        .filter(location => location.country === internalSelectedCountry.value)
        .map(location => ({
          name: location.city,
          count: location.materials_count || 1
        }))
        .sort((a, b) => b.count - a.count);
    });

    const popularLocations = computed(() => {
      if (!props.locations || !Array.isArray(props.locations)) return [];
      
      return props.locations
        .filter(location => (location.materials_count || 1) > 1)
        .map(location => ({
          country: location.country,
          city: location.city,
          count: location.materials_count || 1
        }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    });

    // Методы
    const getCountryMaterialsCount = (country) => {
      if (!props.locations) return 0;
      
      return props.locations
        .filter(location => location.country === country)
        .reduce((total, location) => total + (location.materials_count || 1), 0);
    };

    const getCityMaterialsCount = (country, city) => {
      if (!props.locations) return 0;
      
      const location = props.locations.find(loc => 
        loc.country === country && loc.city === city
      );
      
      return location ? (location.materials_count || 1) : 0;
    };

    const onCountryChange = () => {
      internalSelectedCity.value = '';
      emit('country-selected', internalSelectedCountry.value);
      emit('city-selected', '');
    };

    const onCityChange = () => {
      emit('city-selected', internalSelectedCity.value);
    };

    const selectLocation = (country, city) => {
      internalSelectedCountry.value = country;
      internalSelectedCity.value = city;
      emit('country-selected', country);
      emit('city-selected', city);
    };

    const showAddModal = () => {
      emit('add-location');
    };

    // Синхронизация с родительскими пропсами
    watch(() => props.selectedCountry, (newCountry) => {
      internalSelectedCountry.value = newCountry;
    });

    watch(() => props.selectedCity, (newCity) => {
      internalSelectedCity.value = newCity;
    });

    return {
      internalSelectedCountry,
      internalSelectedCity,
      countries,
      availableCities,
      popularLocations,
      getCountryMaterialsCount,
      getCityMaterialsCount,
      onCountryChange,
      onCityChange,
      selectLocation,
      showAddModal
    };
  }
};
</script>

<style scoped>
.location-filter-widget .card {
  border: 1px solid var(--yt-border-color, #dee2e6);
}

.card-header {
  background-color: var(--yt-bg-secondary, #f8f9fa);
  border-bottom: 1px solid var(--yt-border-color, #dee2e6);
}

.location-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.location-badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  white-space: nowrap;
  border-radius: 12px;
  transition: all 0.2s ease;
}

.location-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.location-badge .badge {
  font-size: 0.65rem;
}

.stats-summary {
  font-size: 0.8rem;
}

.form-select {
  font-size: 0.85rem;
}

.add-location {
  border-top: 1px solid var(--yt-bg-tertiary, #e9ecef);
  padding-top: 10px;
}

.add-location .btn {
  font-size: 0.8rem;
  color: var(--yt-text-muted, #6c757d);
}

.add-location .btn:hover {
  color: #0d6efd;
}

@media (max-width: 576px) {
  .location-badges {
    flex-direction: column;
  }

  .location-badge {
    font-size: 0.8rem;
    padding: 6px 10px;
  }
}

/* Dark mode support */
html[data-bs-theme="dark"] .location-filter-widget .card {
  border-color: var(--yt-border-color, #495057);
}

html[data-bs-theme="dark"] .card-header {
  background-color: var(--yt-bg-secondary, #343a40);
  border-bottom-color: var(--yt-border-color, #495057);
}

html[data-bs-theme="dark"] .add-location {
  border-top-color: var(--yt-bg-tertiary, #495057);
}

html[data-bs-theme="dark"] .add-location .btn {
  color: var(--yt-text-muted, #adb5bd);
}

html[data-bs-theme="dark"] .location-badge:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
</style>